import type { FlightItineraryDetailSliceSummaryProps } from "@apac/components/flights";
import {
  FlightScheduleChangeBadge,
  FlightScheduleChangeModal,
  useFlightItineraryDetailSliceSummary,
} from "@b2bportal/core-flights";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { AirlineIcon, Icon } from "@b2bportal/core-ui";
import { isMixedCabinSegments } from "@b2bportal/core-utilities";
import { DateTimeFormatStyle, Trans, useI18nContext } from "@hopper-b2b/i18n";
import { removeTimezone } from "@hopper-b2b/utilities";
import clsx from "clsx";
import dayjs from "dayjs";

import { ApacFlightsComponent } from "../../context";
import { useApacFlightsStyles } from "../../hooks";
import defaultStyles from "./style.module.scss";

export const FlightItineraryDetailSliceSummary = ({
  className,
  titleIcon,
  ...props
}: FlightItineraryDetailSliceSummaryProps) => {
  const COMPONENT_KEY = ApacFlightsComponent.FlightItineraryDetailSliceSummary;
  const styles = useApacFlightsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { context, text } = useFlightItineraryDetailSliceSummary(props);
  const { t, formatDateTime } = useI18nContext();

  // Source the arrival-side values from the slice's LAST segment so multi-leg
  // slices show the destination's clock and date, not the intermediate stop.
  // The core hook's context.arrivalTime is derived from firstSegment which is
  // incorrect for multi-segment slices; we recompute locally to avoid that.
  const { segments } = props.slice;
  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];
  const isMixedCabin = isMixedCabinSegments(segments);
  const departureISO =
    firstSegment.updatedDeparture ?? firstSegment.scheduledDeparture;
  const arrivalISO = lastSegment.updatedArrival ?? lastSegment.scheduledArrival;

  // Both times are formatted from the airport-local wall clock (offset
  // stripped) so a viewer in any timezone sees the boarding-pass times. The
  // core hook's context.departureTime/arrivalTime convert to the browser's
  // timezone, which would render a non-local user a nonsensical pair like
  // "11:00 PM → 8:15 PM" for a westbound flight viewed from the east.
  const departureTime = formatDateTime(
    dayjs(removeTimezone(departureISO)).toDate(),
    DateTimeFormatStyle.ShortTime
  );
  const arrivalTime = formatDateTime(
    dayjs(removeTimezone(arrivalISO)).toDate(),
    DateTimeFormatStyle.ShortTime
  );
  const plusDays = dayjs(removeTimezone(arrivalISO))
    .startOf("day")
    .diff(dayjs(removeTimezone(departureISO)).startOf("day"), "day");
  const plusDaysLabel =
    plusDays > 0
      ? t("flightArrivalDifferentDay.plusDaysCount", {
          count: plusDays,
          defaultValue: `+${plusDays} day${plusDays === 1 ? "" : "s"}`,
        })
      : null;
  const arrivalDateLabel =
    plusDays !== 0
      ? formatDateTime(
          dayjs(removeTimezone(arrivalISO)).toDate(),
          DateTimeFormatStyle.MediumDate
        )
      : null;

  return (
    <div className={clsx(block, className)}>
      <div className={cn("title")}>
        {titleIcon}
        {`${text.title} ${text.departureDate}`}
      </div>
      <div
        className={cn("info-wrapper", {
          "major-schedule-change": context.hasMajorScheduleChange,
        })}
      >
        {context.sliceChangeContext?.severity != null && (
          <FlightScheduleChangeBadge
            severity={context.sliceChangeContext.severity}
          />
        )}
        <div className={cn("slice-summary")}>
          <div
            className={cn("departure-arrival", {
              "major-schedule-change": context.hasMajorScheduleChange,
            })}
          >
            <div>{departureTime}</div>
            <Icon iconName={IconNameEnum.airplane} />
            <div>{arrivalTime}</div>
            {plusDaysLabel != null ? (
              <span className={cn("plus-days")}>{plusDaysLabel}</span>
            ) : null}
          </div>
          {arrivalDateLabel != null ? (
            <div className={cn("arrives-on")}>
              <Trans
                i18nKey="flightArrivalDifferentDay.flightArrivesOnDate"
                values={{ arrivalDate: arrivalDateLabel }}
                components={{
                  1: <span className={cn("notification-date")} />,
                }}
              />
            </div>
          ) : null}
          <div className={cn("duration")}>{text.duration}</div>
          <div className={cn("stops")}>{text.stops}</div>

          <div className={cn("airline")}>
            <AirlineIcon
              className={cn("icon")}
              airlineCode={context.airlineCode}
            />
            {text.airline} • {context.airlineCode} {context.flightNumber}
          </div>
        </div>

        {isMixedCabin ? (
          <span className={cn("mixed-cabin-pill")}>{t("mixedCabin.icon")}</span>
        ) : null}

        {context.hasMajorScheduleChange &&
          props.scheduleChangeDiffs != null && (
            <div className={cn("slice-cta")}>
              <FlightScheduleChangeModal
                hoursToDeparture={props.itinerary.hoursToDeparture}
                itinerary={props.itinerary.bookedItinerary}
                scheduleChangeDiffs={props.scheduleChangeDiffs}
                triggerCtaLabel={text.scheduleChangeCtaLabel}
              />
            </div>
          )}
      </div>
    </div>
  );
};
